<template>
  <div ref="root" class="fs-menu" @keydown="onKeydown">
    <LgButton size="sm" variant="ghost" :aria-pressed="open" @click="toggleOpen">
      <template #icon><ListFilter :size="14" aria-hidden="true" /></template>
      Saved filters
      <ChevronDown :size="13" aria-hidden="true" />
    </LgButton>

    <div v-if="open" class="panel" role="menu" aria-label="Saved filter sets">
      <button type="button" class="save-current" @click="openSaveModal">
        <Plus :size="14" aria-hidden="true" />
        Save current…
      </button>

      <div v-if="filterSets.loading.value" class="panel-note">Loading…</div>
      <div v-else-if="filterSets.error.value" class="panel-note err">
        Couldn't load saved filters.
      </div>
      <div v-else-if="!ordered.length" class="panel-note">No saved filters yet.</div>

      <ul v-else class="set-list">
        <li v-for="s in ordered" :key="s.id" class="set-item">
          <button
            type="button"
            class="star-btn"
            :class="{ on: isFavorite(s.id) }"
            :aria-pressed="isFavorite(s.id)"
            :aria-label="isFavorite(s.id) ? `Unfavorite ${s.name}` : `Favorite ${s.name}`"
            :title="isFavorite(s.id) ? 'Unfavorite' : 'Favorite'"
            @click.stop="onToggleFavorite(s.id)"
          >
            <Star :size="14" aria-hidden="true" />
          </button>

          <button type="button" class="set-name" @click="applySet(s)">{{ s.name }}</button>

          <!-- Delete confirm -->
          <span v-if="confirmDeleteId === s.id" class="confirm-inline">
            <span class="confirm-q">Delete?</span>
            <button type="button" class="confirm-yes" @click.stop="onConfirmDelete(s.id)">
              Delete
            </button>
            <button type="button" class="confirm-no" @click.stop="confirmDeleteId = null">
              Cancel
            </button>
          </span>
          <!-- Update-to-current confirm (overwrites this set with the live filters) -->
          <span v-else-if="confirmUpdateId === s.id" class="confirm-inline">
            <span class="confirm-q">Overwrite?</span>
            <button
              type="button"
              class="confirm-yes update"
              :disabled="updatingId === s.id"
              @click.stop="onConfirmUpdate(s)"
            >
              {{ updatingId === s.id ? 'Saving…' : 'Update' }}
            </button>
            <button
              type="button"
              class="confirm-no"
              @click.stop="
                confirmUpdateId = null;
                updateError = '';
              "
            >
              Cancel
            </button>
          </span>
          <!-- Default: both actions are visibly present per row -->
          <span v-else class="set-actions">
            <button
              type="button"
              class="action-btn update-btn"
              :aria-label="`Update ${s.name} to the current filters`"
              title="Update to current filters"
              @click.stop="startUpdate(s.id)"
            >
              <RefreshCw :size="14" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="action-btn delete-btn"
              :aria-label="`Delete ${s.name}`"
              title="Delete"
              @click.stop="startDelete(s.id)"
            >
              <Trash2 :size="14" aria-hidden="true" />
            </button>
          </span>
        </li>
      </ul>

      <p v-if="updateError" class="panel-note err" role="alert">{{ updateError }}</p>
    </div>

    <LgModal :open="saveModalOpen" title="Save current filters" @close="closeSaveModal">
      <div class="save-body">
        <LgInput
          v-model="saveName"
          placeholder="Filter set name"
          aria-label="Filter set name"
          @keydown.enter.prevent="onSave"
        />
        <p v-if="existingMatch" class="panel-note warn-note">
          A filter set named "{{ saveName.trim() }}" already exists — saving will overwrite it.
        </p>
        <p v-if="saveError" class="form-msg form-error" role="alert">{{ saveError }}</p>
      </div>
      <template #footer>
        <LgButton size="sm" variant="ghost" @click="closeSaveModal">Cancel</LgButton>
        <LgButton
          size="sm"
          variant="primary"
          :disabled="!saveName.trim() || saving"
          @click="onSave"
        >
          {{ saving ? 'Saving…' : existingMatch ? 'Overwrite' : 'Save' }}
        </LgButton>
      </template>
    </LgModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch } from 'vue';
import { ListFilter, ChevronDown, Star, RefreshCw, Trash2, Plus } from 'lucide-vue-next';
import { ApiError, type FilterSet } from '../api.js';
import { normalizeFilterSetQuery } from '../composables/useFilters.js';
import { useFilterSets, sortFilterSets } from '../composables/useFilterSets.js';
import LgButton from '../ui/LgButton.vue';
import LgInput from '../ui/LgInput.vue';
import LgModal from '../ui/LgModal.vue';

const props = defineProps<{
  /** Current filter state serialized via `filtersToQuery` — what "Save current…" persists. */
  query: string;
}>();

const emit = defineEmits<{ apply: [query: string] }>();

/**
 * The server (`POST /api/filter-sets`) rejects a query over 2048 chars with an
 * opaque 400. Refuse a little under that (headroom for the normalize round-trip
 * being non-shrinking) with a friendly inline message instead — five 256-byte
 * text terms plus five attrs plus eight pins can serialize past the cap.
 */
const MAX_SAVED_QUERY_LEN = 2000;
const TOO_LARGE_MSG = 'This filter is too large to save — remove some terms.';

const filterSets = useFilterSets();

const open = ref(false);
const confirmDeleteId = ref<number | null>(null);
const confirmUpdateId = ref<number | null>(null);
const updatingId = ref<number | null>(null);
/** Inline error for the "Update to current" path (which has no modal to host it). */
const updateError = ref('');

const saveModalOpen = ref(false);
const saveName = ref('');
const saveError = ref('');
const saving = ref(false);

const ordered = computed(() => sortFilterSets(filterSets.sets.value, filterSets.favorites.value));

function isFavorite(id: number): boolean {
  return filterSets.favorites.value.includes(id);
}

function toggleOpen() {
  if (open.value) {
    close();
  } else {
    open.value = true;
    confirmDeleteId.value = null;
    confirmUpdateId.value = null;
    filterSets.load();
  }
}

function close() {
  open.value = false;
  confirmDeleteId.value = null;
  confirmUpdateId.value = null;
}

// The two per-row confirms are mutually exclusive — opening one dismisses the
// other so a row never shows two "Are you sure?" prompts at once.
function startDelete(id: number) {
  confirmUpdateId.value = null;
  updateError.value = '';
  confirmDeleteId.value = id;
}
function startUpdate(id: number) {
  confirmDeleteId.value = null;
  updateError.value = '';
  confirmUpdateId.value = id;
}

function applySet(s: FilterSet) {
  emit('apply', s.query);
  close();
}

async function onToggleFavorite(id: number) {
  try {
    await filterSets.toggleFavorite(id);
  } catch {
    /* optimistic update already rolled back inside useFilterSets */
  }
}

async function onConfirmDelete(id: number) {
  try {
    await filterSets.remove(id);
  } catch {
    /* optimistic update already rolled back inside useFilterSets */
  }
  confirmDeleteId.value = null;
}

/**
 * "Update to current": re-save the live filter state under this set's existing
 * NAME. `filterSets.save` is upsert-by-name server-side, so the set's id stays
 * stable — this overwrites the query in place, no delete/recreate. The query is
 * normalized identically to "Save current…" so both paths persist the canonical
 * v0.3 shape (pins + negation included, liveQuery/cursor stripped).
 */
async function onConfirmUpdate(s: FilterSet) {
  const normalized = normalizeFilterSetQuery(props.query);
  if (normalized.length > MAX_SAVED_QUERY_LEN) {
    updateError.value = TOO_LARGE_MSG;
    return;
  }
  updateError.value = '';
  updatingId.value = s.id;
  try {
    await filterSets.save(s.name, normalized);
  } catch {
    /* leave the set unchanged; save() only mutates the list after the POST resolves */
  }
  updatingId.value = null;
  confirmUpdateId.value = null;
}

const existingMatch = computed(() =>
  filterSets.sets.value.find((s) => s.name === saveName.value.trim()),
);

function openSaveModal() {
  saveName.value = '';
  saveError.value = '';
  saving.value = false;
  saveModalOpen.value = true;
}

function closeSaveModal() {
  saveModalOpen.value = false;
}

async function onSave() {
  const name = saveName.value.trim();
  if (!name) return;
  const normalized = normalizeFilterSetQuery(props.query);
  if (normalized.length > MAX_SAVED_QUERY_LEN) {
    saveError.value = TOO_LARGE_MSG;
    return;
  }
  saving.value = true;
  saveError.value = '';
  try {
    await filterSets.save(name, normalized);
    saveModalOpen.value = false;
    open.value = false;
  } catch (e) {
    saveError.value =
      e instanceof ApiError
        ? e.message
        : e instanceof Error
          ? e.message
          : 'Failed to save filter set.';
  } finally {
    saving.value = false;
  }
}

// Escape closes the menu, but not while the save modal (which handles its own
// Escape) is open on top of it.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value && !saveModalOpen.value) {
    e.preventDefault();
    close();
  }
}

const root = ref<HTMLElement | null>(null);
function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));

// The per-row confirms are momentary — collapse them if the menu itself closes
// so re-opening never shows a stale "Delete?"/"Overwrite?" state.
watch(open, (isOpen) => {
  if (!isOpen) {
    confirmDeleteId.value = null;
    confirmUpdateId.value = null;
    updateError.value = '';
  }
});
</script>

<style scoped>
.fs-menu {
  position: relative;
  display: inline-flex;
}

.panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  width: 260px;
  max-width: calc(100vw - var(--lg-space-6));
  display: flex;
  flex-direction: column;
  padding: var(--lg-space-1);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}

/* Mobile: viewport bottom-sheet instead of a trigger-anchored panel (see the same
   note in AttrFilterDropdown) so it never clips off the right edge at 390px. */
@media (max-width: 768px) {
  .panel {
    position: fixed;
    left: var(--lg-space-2);
    right: var(--lg-space-2);
    bottom: var(--lg-space-2);
    top: auto;
    width: auto;
    max-width: none;
    max-height: 75vh;
    overflow-y: auto;
  }
}

.save-current {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  width: 100%;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  border: none;
  border-radius: var(--lg-radius-sm);
  border-bottom: 1px solid var(--lg-border-faint);
  background: transparent;
  color: var(--lg-accent);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.save-current:hover {
  background: var(--lg-bg-hover);
}

.panel-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.panel-note.err {
  color: var(--lg-danger);
}
.panel-note.warn-note {
  color: var(--lg-warn);
  padding: 0;
  margin: var(--lg-space-2) 0 0;
}

.set-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 300px;
  overflow-y: auto;
}
.set-item {
  display: flex;
  align-items: center;
  gap: var(--lg-space-1);
}
.star-btn,
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--lg-control-sm);
  height: var(--lg-control-sm);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-faint);
  cursor: pointer;
}
.star-btn:hover,
.action-btn:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.star-btn.on {
  color: var(--lg-warn);
}
.set-actions {
  display: inline-flex;
  flex-shrink: 0;
  gap: 2px;
}
.delete-btn:hover {
  color: var(--lg-danger);
}
.set-name {
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.set-name:hover {
  background: var(--lg-bg-hover);
}

.confirm-inline {
  display: flex;
  align-items: center;
  gap: var(--lg-space-1);
  flex-shrink: 0;
  padding-right: var(--lg-space-1);
}
.confirm-q {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}
.confirm-yes,
.confirm-no {
  border: none;
  border-radius: var(--lg-radius-sm);
  font: inherit;
  font-size: var(--lg-fs-xs);
  padding: var(--lg-space-1) var(--lg-space-2);
  cursor: pointer;
}
.confirm-yes {
  background: var(--lg-danger);
  color: var(--lg-bg);
}
.confirm-yes.update {
  background: var(--lg-accent);
}
.confirm-yes:disabled {
  opacity: 0.6;
  cursor: default;
}
.confirm-no {
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  border: 1px solid var(--lg-border-strong);
}

.save-body {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.form-msg {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.form-error {
  color: var(--lg-danger);
}
</style>
